import { useEffect, useState } from 'react';
import { showToast } from '../config/toast.config';
import PropTypes from 'prop-types';
import { authService } from '../services/api';
import { isKioskMode } from '../services/api/kiosk.service';
import { AuthContext } from './auth-context';

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // One expired session surfaces as a burst of failures — every request in
    // flight reports it — and each used to fire its own logout POST and its own
    // redirect. Tear the session down once.
    let forceLogoutInFlight = false;
    const forceLogout = async () => {
      // On a public terminal the kiosk lock overlay IS the logged-out state.
      // Tearing down auth state here and hard-redirecting to /login would
      // reload the page, and that reload is exactly what destroys the route +
      // form state we restore when the same person re-authorizes. The overlay
      // already covers the screen and the session is closed server-side.
      if (isKioskMode()) {
        return;
      }
      if (forceLogoutInFlight) {
        return;
      }
      forceLogoutInFlight = true;
      try {
        await authService.logout();
      } catch (logoutError) {
        console.error('Force logout failed:', logoutError);
        showToast.error(logoutError);
      } finally {
        setIsAuthenticated(false);
        setPermissions([]);
        setRoles([]);
        setUser(null);
        setIsLoading(false);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userRoles');
        // The two kiosk pages are legitimately reachable while logged out: the
        // terminal's enrollment landing page, and the authorize page a phone
        // opens after scanning a QR. Redirecting them to /login would abort the
        // enrollment mid-request and would drop the return_to that brings the
        // phone back here after signing in. Auth state is still cleared above;
        // only the navigation is skipped.
        const path = window.location.pathname;
        const isPublicKioskPath = path === '/kiosk/start' || path.startsWith('/p/');
        if (path !== '/login' && !isPublicKioskPath) {
          window.location.href = '/login?session_expired=true';
        } else {
          forceLogoutInFlight = false;
        }
      }
    };

    // The boot refresh often runs before a phone's radio is back up, and a
    // single failed fetch used to leave a perfectly valid session sitting at the
    // login screen. Retry the transient failures; a 401 means the session is
    // genuinely over, so stop immediately and keep the login screen instant.
    const refreshWithRetry = async () => {
      const backoffMs = [400, 1200];
      for (let attempt = 0; ; attempt++) {
        const token = await authService.refreshAccessToken();
        if (token) return token;
        if (authService.getLastRefreshFailure() === 'auth') return null;
        if (attempt >= backoffMs.length) return null;
        await new Promise(resolve => setTimeout(resolve, backoffMs[attempt]));
      }
    };

    let bootstrapping = false;
    const bootstrap = async () => {
      if (bootstrapping) {
        return;
      }
      bootstrapping = true;
      try {
        // Always attempt to refresh using HttpOnly cookie on load
        const access = await refreshWithRetry();

        // Nothing to work with: either the session is over, or the device is
        // still offline. Either way the refresh cookie stays untouched, so a
        // reload — or the 'online' retry below — can pick the session back up.
        if (!access) {
          return;
        }

        const meResp = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}${'/stoc/auth/v2/me'}`, {
          headers: { Authorization: `Bearer ${access}` },
          credentials: 'include'
        });

        // Only the server rejecting the identity ends the session. A 5xx or a
        // Cloudflare error page says nothing about whether the user is still
        // logged in, and tearing down over one costs the refresh cookie.
        if (meResp.status === 401 || meResp.status === 403) {
          console.warn('Session rejected by /me, forcing logout.', meResp.status);
          await forceLogout();
          return;
        }

        if (!meResp.ok) {
          console.warn('Failed to hydrate user from /me, keeping session.', meResp.status);
          return;
        }

        const me = await meResp.json();
        const roleNames = Array.isArray(me.roles) ? me.roles : [];
        const perms = Array.isArray(me.permissions) ? me.permissions : [];
        const normalizedUsername = typeof me.username === 'string' ? me.username.trim() : '';

        if (!normalizedUsername) {
          console.warn('Missing username in /me response, forcing logout.');
          await forceLogout();
          return;
        }

        setPermissions(perms);
        setRoles(roleNames);
        setUser({
          userId: me.user_id,
          username: normalizedUsername,
          firstName: me.personal_data?.firstName,
          lastName: me.personal_data?.lastName
        });
        setIsAuthenticated(true);
      } catch (err) {
        // A connection that dropped mid-hydration says nothing about the
        // session. Keep it and let the user — or the 'online' listener —
        // try again, instead of spending the refresh cookie on a blip.
        console.error('Auth bootstrap failed:', err);
      } finally {
        bootstrapping = false;
        setIsLoading(false);
      }
    };

    const checkAuth = () => {
      // Don't treat local storage as source of truth for auth; it's only for UI fallback
      // Start unauthenticated; we'll hydrate from /me
      setIsAuthenticated(false);
      setPermissions([]);
      setRoles([]);
      bootstrap();
    };

    checkAuth();

    // Back online, or back in the foreground, after the app sat in the
    // background: if the boot refresh ran before the phone had a connection,
    // this is what restores the session instead of asking for the password.
    // Resuming a frozen tab does not always fire 'online', hence both.
    const retryBootstrap = () => {
      // The cookie is known to be spent — don't re-probe on every focus.
      if (authService.getLastRefreshFailure() === 'auth') {
        return;
      }
      if (!authService.isAuthenticated()) {
        bootstrap();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        retryBootstrap();
      }
    };
    window.addEventListener('online', retryBootstrap);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle session expiration
    const handleSessionExpired = () => {
      console.info('Session expired, logging out');
      forceLogout();
    };
    
    // Listen for token refresh events
    const handleTokenRefreshed = (event) => {
      console.info('Token refreshed event received');
      if (event.detail?.accessToken) {
        setIsAuthenticated(true);
      }
    };
    
    // Listen for session expiration events
    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    window.addEventListener('auth:tokenRefreshed', handleTokenRefreshed);

    // No cross-tab storage sync; auth state is in memory
    
    return () => {
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
      window.removeEventListener('auth:tokenRefreshed', handleTokenRefreshed);
      window.removeEventListener('online', retryBootstrap);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const hasRole = (role) => {
    return roles.includes(role);
  };

  const login = async (credentials) => {
    const response = await authService.login(credentials);
    if (response && response.accessToken) {
      const roleNames = Array.isArray(response.user?.roles)
        ? response.user.roles.map(r => r.roleName || r)
        : [];
      const perms = Array.isArray(response.user?.permissions) ? response.user.permissions : [];
      setPermissions(perms);
      setRoles(roleNames);
      setUser({ userId: response.user?.user_id, username: response.user?.username, firstName: response.user?.personal_data?.firstName, lastName: response.user?.personal_data?.lastName });
      setIsAuthenticated(true);
      return response;
    }
    throw new Error('Invalid response from server');
  };

  const logout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      // No roles/permissions persisted
      setIsAuthenticated(false);
      setPermissions([]);
      setRoles([]);
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      showToast.error(error);
      // Still remove tokens even if the API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('userRoles');
      setIsAuthenticated(false);
      setPermissions([]);
      setRoles([]);
      setUser(null);
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      const newToken = await authService.refreshAccessToken();
      return !!newToken;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      showToast.error(error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      login, 
      logout, 
      refreshToken,
      hasPermission,
      hasRole,
      permissions,
      roles,
      user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider; 